import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { adminModulesApi, TechInventoryReportRow } from '../api/adminModules';

// Fixed column order matches the discovery lifecycle system-inventory list.
const CATEGORIES = [
  'CRM',
  'Email Marketing',
  'PMS / Financial',
  'ERM',
  'HR',
  'Data Enrichment',
  'Identity/Directory',
  'Other',
] as const;
type Category = typeof CATEGORIES[number];

const STATUS_STYLE: Record<'active' | 'planned' | 'retiring', { bg: string; fg: string; border: string; label: string }> = {
  active:   { bg: '#e6f5ea', fg: '#1b7a3a', border: '#8fce9f', label: 'Active' },
  planned:  { bg: '#fff6d6', fg: '#8a6300', border: '#e6c94a', label: 'Planned' },
  retiring: { bg: '#fde7e7', fg: '#a11414', border: '#f0a5a5', label: 'Retiring' },
};

export default function AdminReportsSystems() {
  const { getToken } = useAuth();
  const [rows, setRows] = useState<TechInventoryReportRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const t = await getToken();
        setRows(await adminModulesApi.reportsTechInventory(t));
      } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    })();
  }, [getToken]);

  // Group rows by client, then by category. Each cell is a list of assets.
  const matrix = useMemo(() => {
    if (!rows) return null;
    // client_id -> { name, slug, cells: Map<category, TechInventoryReportRow[]> }
    const byClient = new Map<string, { name: string; slug: string; cells: Map<Category, TechInventoryReportRow[]> }>();
    for (const r of rows) {
      let entry = byClient.get(r.client_id);
      if (!entry) {
        entry = { name: r.client_name, slug: r.client_slug, cells: new Map() };
        for (const c of CATEGORIES) entry.cells.set(c, []);
        byClient.set(r.client_id, entry);
      }
      // Rows without an asset (client with zero inventory) still register the
      // client but don't add anything to a cell.
      if (r.id && r.category && (CATEGORIES as readonly string[]).includes(r.category)) {
        entry.cells.get(r.category as Category)!.push(r);
      }
    }
    // Sort clients alphabetically. Backend already returns them sorted, but
    // this makes the invariant explicit.
    return Array.from(byClient.entries())
      .map(([id, v]) => ({ id, ...v }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  if (error) return <div className="card" style={{ color: '#a11414' }}>Error: {error}</div>;
  if (!matrix) return <div className="card">Loading…</div>;

  const totalAssets = rows?.filter((r) => r.id).length ?? 0;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ color: '#666', fontSize: 13 }}>
          {matrix.length} client{matrix.length === 1 ? '' : 's'} · {totalAssets} system{totalAssets === 1 ? '' : 's'} recorded
        </div>
        <Legend />
      </div>

      <div style={{ overflowX: 'auto', border: '1px solid #e0e0e0', borderRadius: 6 }}>
        <table style={{
          borderCollapse: 'collapse', width: '100%', fontSize: 13,
          background: 'white',
        }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, textAlign: 'left', position: 'sticky', left: 0, background: '#f6f7f9', zIndex: 2 }}>
                Client
              </th>
              {CATEGORIES.map((c) => (
                <th key={c} style={thStyle}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matrix.length === 0 && (
              <tr>
                <td colSpan={CATEGORIES.length + 1} style={{ padding: 24, textAlign: 'center', color: '#999' }}>
                  No clients yet.
                </td>
              </tr>
            )}
            {matrix.map((client) => (
              <tr key={client.id}>
                <td style={{ ...tdStyle, fontWeight: 500, textAlign: 'left', position: 'sticky', left: 0, background: 'white', zIndex: 1 }}>
                  {client.name}
                </td>
                {CATEGORIES.map((c) => {
                  const items = client.cells.get(c) ?? [];
                  return (
                    <td key={c} style={tdStyle}>
                      {items.length === 0
                        ? <span style={{ color: '#ccc' }}>—</span>
                        : <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {items.map((a) => <SystemPill key={a.id!} asset={a} />)}
                          </div>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SystemPill({ asset }: { asset: TechInventoryReportRow }) {
  const status = asset.status ?? 'active';
  const style = STATUS_STYLE[status];
  const label = [asset.vendor, asset.version].filter(Boolean).join(' ') || asset.name || '(unnamed)';
  const title = [
    `Status: ${style.label}`,
    asset.phase ? `Phase: ${asset.phase}` : null,
    asset.owner ? `Owner: ${asset.owner}` : null,
    asset.notes ? `Notes: ${asset.notes}` : null,
  ].filter(Boolean).join('\n');
  return (
    <span
      title={title}
      style={{
        display: 'inline-block',
        background: style.bg, color: style.fg,
        border: `1px solid ${style.border}`,
        borderRadius: 4, padding: '2px 8px', fontSize: 12,
        whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden',
        maxWidth: 180,
      }}
    >
      {label}
    </span>
  );
}

function Legend() {
  return (
    <div style={{ display: 'flex', gap: 10, fontSize: 12, color: '#666' }}>
      {(['active', 'planned', 'retiring'] as const).map((s) => (
        <span key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span style={{
            width: 12, height: 12, borderRadius: 3,
            background: STATUS_STYLE[s].bg, border: `1px solid ${STATUS_STYLE[s].border}`,
          }} />
          {STATUS_STYLE[s].label}
        </span>
      ))}
    </div>
  );
}

const thStyle: React.CSSProperties = {
  background: '#f6f7f9', padding: '10px 12px',
  borderBottom: '1px solid #e0e0e0', borderRight: '1px solid #ececec',
  fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4,
  color: '#555', fontWeight: 600, textAlign: 'center',
  verticalAlign: 'bottom',
};
const tdStyle: React.CSSProperties = {
  padding: '8px 12px', borderTop: '1px solid #eee', borderRight: '1px solid #f2f2f2',
  verticalAlign: 'top', textAlign: 'center',
};
